// Per-page SEO helpers. Competition SEO is stored in the shared backend via
// metadata_tags (no backend schema change needed):
//   seo_title:<title>
//   seo_desc:<meta description>
//   seo_kw:<comma,separated,keywords>
import { useEffect } from "react";

export const SEO_TITLE_PREFIX = "seo_title:";
export const SEO_DESC_PREFIX = "seo_desc:";
export const SEO_KW_PREFIX = "seo_kw:";

const tagValue = (tags, prefix) => {
  const t = (tags || []).find((x) => typeof x === "string" && x.startsWith(prefix));
  return t ? t.slice(prefix.length) : "";
};

export const getSeo = (comp) => {
  const tags = comp?.metadata_tags || [];
  return {
    title: tagValue(tags, SEO_TITLE_PREFIX),
    desc: tagValue(tags, SEO_DESC_PREFIX),
    keywords: tagValue(tags, SEO_KW_PREFIX),
  };
};

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

// Lightweight per-page SEO without extra dependencies.
export const useDocumentSeo = ({ title, description, keywords, canonical, image } = {}) => {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;
    if (description) {
      setMeta("description", description);
      setMeta("og:title", title, "property");
      setMeta("og:description", description, "property");
      setMeta("og:type", "website", "property");
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", title);
      setMeta("twitter:description", description);
    }
    if (keywords) setMeta("keywords", keywords);
    if (canonical) { setLink("canonical", canonical); setMeta("og:url", canonical, "property"); }
    if (image) { setMeta("og:image", image, "property"); setMeta("twitter:image", image); }
    return () => {
      document.title = prevTitle;
    };
  }, [title, description, keywords, canonical, image]);
};

// Injects backend-provided schema.org JSON-LD (Product/Offer, BreadcrumbList, FAQPage)
// into the page <head> so Google can render rich results. Cleans up on unmount.
export const useJsonLd = (data) => {
  useEffect(() => {
    if (!data) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-rrc-jsonld", "1");
    el.textContent = typeof data === "string" ? data : JSON.stringify(data);
    document.head.appendChild(el);
    return () => { try { document.head.removeChild(el); } catch (e) { /* noop */ } };
  }, [data]);
};
